/**
 * The AI-SDK <-> MUI X ChatBox seam: wraps `createAiSdkAdapter` and normalizes every stream via `./normalize-chunks`.
 * `onFinishMetadata` taps the finish chunk's `{threadId, messageId}` (the MUI runtime drops it) for whichever transport is plugged in.
 */

import { createAiSdkAdapter } from "@mui/x-chat"
import type { AiSdkUIMessageChunk, CreateAiSdkAdapterRequest } from "@mui/x-chat"
import type { ChatAdapter, ChatDynamicToolMessagePart, ChatMessage, ChatMessagePart, ChatToolInvocationState } from "@mui/x-chat-headless"
import { isArtifactEnvelope } from "./artifact-contract"
import { type ChatFinishMetadata, settleFinalAnswerParts, normalizeChunkStream } from "./normalize-chunks"

export type ChatboxUIMessageStream = ReadableStream<AiSdkUIMessageChunk>

export type ChatboxStreamFactory = (
  request: CreateAiSdkAdapterRequest,
) => Promise<ChatboxUIMessageStream> | ChatboxUIMessageStream

export interface ChatboxAdapterOptions {
  /** Invoked with each finish chunk's metadata and the adapter request that produced the turn. */
  onFinishMetadata: (metadata: ChatFinishMetadata, request: CreateAiSdkAdapterRequest) => void
  /**
   * Invoked with the new thread's id and the request the moment the `thread_created` event arrives — before
   * finish. Returns the id this turn actually adopted (or `null`/nothing if it declined), which the adapter
   * captures in THIS turn's closure and hands to `onTurnSettled` — so settle rolls back the adoption of the turn
   * it belongs to, never one a later turn clobbered in a shared ref (the endTurn-before-flush race, APP-3404 R1).
   */
  onEarlyThreadId: (threadId: string, request: CreateAiSdkAdapterRequest) => string | null | undefined
  /** Invoked with the assistant row a failed turn persisted and its thread, so the next send parents on the real head. */
  onFailedTurnAssistantId: (assistantMessageId: string, threadId: string | null) => void
  /**
   * The active turn's abort signal. Captured per-turn at stream start (right after the turn factory begins the
   * turn) so a chunk that parks past a `stopTurn()`/nav abort is dropped even though `request.signal` (ChatBox's
   * for the composer, a throwaway for programmatic sends) never observed that abort.
   */
  getTurnSignal: () => AbortSignal | undefined
  /** Fires once the turn's stream drains, with whether it committed (a finish carrying a message id) and the id this turn early-adopted (or null). */
  onTurnSettled: (committed: boolean, earlyAdoptedThreadId: string | null) => void
}

/** Bridge an AI-SDK UI Message Stream into a MUI X ChatBox `ChatAdapter`, normalized. */
export function createChatboxAdapter(stream: ChatboxStreamFactory, options: ChatboxAdapterOptions): ChatAdapter {
  const { onFinishMetadata, onEarlyThreadId, onFailedTurnAssistantId, getTurnSignal, onTurnSettled } = options
  return createAiSdkAdapter({
    stream: async (request) => {
      const source = await stream(request)
      // Capture THIS turn's controller signal now; the view-mutating taps below must not fire for a turn the
      // user has already stopped or navigated away from (the parked-chunk hijack).
      const turnSignal = getTurnSignal()
      const isTurnAborted = () => turnSignal?.aborted === true
      // Per-turn closure (not a shared ref): the id THIS turn early-adopted, handed to its own settle. endTurn
      // frees the active-turn ref one pipe stage before flush reaches settle, so a next turn could clobber a
      // shared ref before this turn settles — closing over it here keeps settle scoped to its turn (APP-3404 R1).
      let earlyAdoptedThreadId: string | null = null
      // A failed turn that persisted a row means the server kept this turn, so settle must not
      // roll the view back: history owns the prompt and the partial answer from here.
      let failurePersisted = false
      return normalizeChunkStream(source, {
        onFinishMetadata: (metadata) => { if (!isTurnAborted()) onFinishMetadata(metadata, request) },
        onEarlyThreadId: (threadId) => {
          if (isTurnAborted()) return
          const adopted = onEarlyThreadId(threadId, request)
          if (typeof adopted === "string") earlyAdoptedThreadId = adopted
        },
        // A committed finish (messageId present) means the server persisted the thread — keep the adoption even
        // if the user then aborts before the stream drains. Every view-mutating tap suppresses on abort (the
        // parked-chunk hijack), the failed-turn one included. Settle treats a persisted failure as committed: the
        // server kept that turn, so history owns it and the view must not roll back to a blank New chat.
        onSettled: (committed) => onTurnSettled(committed || failurePersisted, earlyAdoptedThreadId),
        onFailedTurnAssistantId: (assistantMessageId, threadId) => {
          if (isTurnAborted()) return
          failurePersisted = true
          onFailedTurnAssistantId(assistantMessageId, threadId)
        },
      })
    },
  })
}

/** One in-flight text part while assembling a message from chunks. */
interface OpenTextPart {
  type: "text"
  text: string
}

/** Schedule a single coalesced progress flush; returns a canceller for the still-pending flush. */
export type ProgressFlushScheduler = (flush: () => void) => () => void

/** Coalesce progress at most once per animation frame — a token stream renders per-frame, not per-token. Falls back to a macrotask where rAF is unavailable (SSR/tests without a DOM). */
const scheduleOnAnimationFrame: ProgressFlushScheduler = (flush) => {
  if (typeof requestAnimationFrame !== "function") {
    const timer = setTimeout(flush, 0)
    return () => clearTimeout(timer)
  }
  const handle = requestAnimationFrame(() => flush())
  return () => cancelAnimationFrame(handle)
}

export interface AssembleAssistantMessageOptions {
  /**
   * Emits a coalesced partial `ChatMessage` snapshot as chunks arrive, so a resumed turn renders token-by-token
   * (APP-3376). Coalesced via `scheduleProgressFlush` (at most one emission per frame, never per token) and never
   * fired after the returned promise resolves — the pending flush is cancelled on drain. Each snapshot is a fresh
   * copy (new parts array + new text parts), so a later token can't mutate an already-emitted view.
   */
  onProgress?: (partial: ChatMessage) => void
  /** Coalescing scheduler for `onProgress`; defaults to requestAnimationFrame. Injectable for deterministic tests. */
  scheduleProgressFlush?: ProgressFlushScheduler
}

/** Assemble one assistant `ChatMessage` from an adapter `sendMessage` stream, so programmatic + resume callers get the composer's part vocabulary — text, `data-artifact`, and `dynamic-tool` parts (the last mirroring MUI's runtime state machine, so a resumed turn's tool calls render like a live one). `onProgress` (opt-in) emits coalesced partial snapshots as chunks arrive. An `error` chunk rejects this call — via the AI SDK adapter stream's own error, or the explicit throw below for a raw normalized stream. */
export async function assembleAssistantMessage(
  stream: ReadableStream<unknown>,
  fallbackMessageId: string,
  options?: AssembleAssistantMessageOptions,
): Promise<ChatMessage> {
  const { onProgress, scheduleProgressFlush = scheduleOnAnimationFrame } = options ?? {}
  const parts: ChatMessagePart[] = []
  const openTextParts = new Map<string, OpenTextPart>()
  // Tool-call parts, keyed by toolCallId so later output chunks advance the same part MUI's runtime builds on a
  // live send. Pushed into `parts` in arrival order (interleaved with text) the first time a tool id is seen.
  const toolParts = new Map<string, ChatDynamicToolMessagePart>()
  let messageId: string | undefined
  let finalAnswerText: string | null = null

  // Get-or-create the dynamic-tool part for a tool call; a fresh part enters `parts` in arrival order.
  const toolPartFor = (toolCallId: string, toolName?: string): ChatDynamicToolMessagePart => {
    const existing = toolParts.get(toolCallId)
    if (existing != null) {
      if (toolName && !existing.toolInvocation.toolName) existing.toolInvocation.toolName = toolName
      return existing
    }
    const part: ChatDynamicToolMessagePart = {
      type: "dynamic-tool",
      toolInvocation: { toolCallId, toolName: toolName ?? "", state: "input-streaming" },
    }
    toolParts.set(toolCallId, part)
    parts.push(part)
    return part
  }
  const advanceToolState = (part: ChatDynamicToolMessagePart, state: ChatToolInvocationState) => {
    part.toolInvocation.state = state
  }
  // Fixed once so streaming snapshots share a stable timestamp (a per-frame `new Date()` would jitter the turn).
  const createdAt = new Date().toISOString()

  // A fresh, independent snapshot: copy the mutated-in-place parts (text and tool) so an emitted partial can't
  // mutate as later chunks land. Artifact parts are immutable envelopes pushed once, so their reference is safe to
  // share; a tool part's `input`/`output` are whole-replaced (never deep-mutated), so a shallow invocation copy suffices.
  const buildSnapshot = (isSettled: boolean): ChatMessage => {
    const snapshotParts: ChatMessagePart[] = parts.map((part) => {
      if (part.type === "text") return { type: "text", text: part.text }
      if (part.type === "dynamic-tool") return { type: "dynamic-tool", toolInvocation: { ...part.toolInvocation } }
      return part
    })
    return {
      id: messageId ?? fallbackMessageId,
      role: "assistant",
      parts: finalAnswerText == null ? snapshotParts : settleFinalAnswerParts(snapshotParts, finalAnswerText),
      createdAt,
      status: isSettled ? "sent" : "streaming",
    }
  }

  // Coalesce progress: each content chunk arms one flush; the canceller holder object avoids a TS narrowing issue
  // (`let cancel` would narrow to `null` at the drain-site `?.()`) and tears down a pending flush on drain.
  let flushArmed = false
  const pending: { cancelFlush: (() => void) | null } = { cancelFlush: null }
  const emitProgress = () => {
    flushArmed = false
    onProgress?.(buildSnapshot(false))
  }
  const scheduleProgress = () => {
    if (!onProgress || flushArmed) return
    flushArmed = true
    pending.cancelFlush = scheduleProgressFlush(emitProgress)
  }

  const reader = stream.getReader()
  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      if (typeof value !== "object" || value === null) continue
      const chunk = value as { type?: unknown, id?: unknown, delta?: unknown, data?: unknown, messageMetadata?: unknown, transient?: unknown, errorText?: unknown, toolCallId?: unknown, toolName?: unknown, input?: unknown, output?: unknown, approvalId?: unknown }
      if (typeof chunk.type !== "string") continue
      const toolName = typeof chunk.toolName === "string" ? chunk.toolName : undefined

      if (chunk.type === "error") {
        // The AI SDK adapter stream already rejects on an error chunk; throw here too so the
        // documented "error → rejection" contract holds for callers passing a raw normalized stream.
        throw new Error(typeof chunk.errorText === "string" ? chunk.errorText : "The assistant stream errored.")
      } else if (chunk.type === "text-start" && typeof chunk.id === "string") {
        const textPart: OpenTextPart = { type: "text", text: "" }
        openTextParts.set(chunk.id, textPart)
        parts.push(textPart)
      } else if (chunk.type === "text-delta" && typeof chunk.id === "string") {
        const textPart = openTextParts.get(chunk.id)
        if (textPart && typeof chunk.delta === "string") {
          textPart.text += chunk.delta
          scheduleProgress()
        }
      } else if (chunk.type === "data-artifact" && chunk.transient !== true && isArtifactEnvelope(chunk.data)) {
        parts.push({ type: "data-artifact", id: chunk.data.id, data: chunk.data })
        scheduleProgress()
      } else if (chunk.type === "tool-input-start" && typeof chunk.toolCallId === "string") {
        advanceToolState(toolPartFor(chunk.toolCallId, toolName), "input-streaming")
        scheduleProgress()
      } else if (chunk.type === "tool-input-available" && typeof chunk.toolCallId === "string") {
        const part = toolPartFor(chunk.toolCallId, toolName)
        part.toolInvocation.input = chunk.input
        advanceToolState(part, "input-available")
        scheduleProgress()
      } else if (chunk.type === "tool-approval-request" && typeof chunk.toolCallId === "string") {
        // Keep the tool visible while it waits on approval; carry the id so a later response resolves the right call.
        const part = toolPartFor(chunk.toolCallId, toolName)
        part.toolInvocation.input = chunk.input
        if (typeof chunk.approvalId === "string") part.toolInvocation.approvalId = chunk.approvalId
        advanceToolState(part, "approval-requested")
        scheduleProgress()
      } else if (chunk.type === "tool-output-available" && typeof chunk.toolCallId === "string") {
        const part = toolPartFor(chunk.toolCallId, toolName)
        part.toolInvocation.output = chunk.output
        advanceToolState(part, "output-available")
        scheduleProgress()
      } else if (chunk.type === "tool-output-error" && typeof chunk.toolCallId === "string") {
        const part = toolPartFor(chunk.toolCallId, toolName)
        if (typeof chunk.errorText === "string") part.toolInvocation.errorText = chunk.errorText
        advanceToolState(part, "output-error")
        scheduleProgress()
      } else if (chunk.type === "tool-output-denied" && typeof chunk.toolCallId === "string") {
        advanceToolState(toolPartFor(chunk.toolCallId, toolName), "output-denied")
        scheduleProgress()
      } else if (chunk.type === "finish") {
        const metadata: unknown = chunk.messageMetadata
        if (typeof metadata === "object" && metadata !== null) {
          const { messageId: finishMessageId, text: finishText } = metadata as { messageId?: unknown, text?: unknown }
          if (typeof finishMessageId === "string") messageId = finishMessageId
          if (typeof finishText === "string") finalAnswerText = finishText
        }
      }
    }
  } finally {
    // Tear down any still-pending flush so no partial lands after the caller has the final message. Cancelling an
    // already-fired frame is a harmless no-op, so this needs no armed-flag guard.
    pending.cancelFlush?.()
  }

  return buildSnapshot(true)
}
